import { FC } from 'react';
import { isMobile } from 'react-device-detect';
import styled, { css, useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    HeaderContainer,
    WinnerModalHeader as JackpotModalHeader,
} from '../WinnerModal';
import {
    CommonModal,
    CommonModalContainer,
    CommonModalProps,
    CloseButtonContainer,
} from '../CommonModal';

type JackpotWinner = {
    name: string;
    amount: string;
};

type JackpotModalProps = CommonModalProps & {
    winners: JackpotWinner[];
    totalJackpotAmount: string;
};

const fontStyleCommon = css`
    font-family: Signika-Bold;
    font-size: 1.7rem;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    text-align: center;
`;

const JackpotModalContainer = styled(CommonModalContainer)`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    position: relative;
    width: 38.75rem;
    min-height: 29.25rem;
    padding: 4.875rem 0 3.5rem 0;
    z-index: -1;

    ${CloseButtonContainer} {
        z-index: 2;
    }

    ${isMobile
        ? ''
        : `
        left: 14rem;
        margin-top: 5.25rem;
   `};
`;

export const JackpotModaTitle = styled.div`
    font-family: Signika-Bold;
    font-size: 3.938rem;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 0.93;
    letter-spacing: normal;
    text-align: center;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    position: relative;
    width: 90%;

    background-image: repeating-linear-gradient(
        to bottom,
        #ffde63,
        #ffc900,
        #ff9003 50%
    );

    &:after {
        background: none;
        content: attr(data-text);
        left: 0;
        top: 0;
        z-index: -1;
        position: absolute;
        text-shadow: 0 -0.125rem 0 #fff, 0 0.313rem 0 #250c61,
            0 -0.438rem 0 #250c61;
    }
`;

export const JackpotModalTotal = styled.div`
    font-family: Signika-Bold;
    font-size: 3.2rem;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    margin-top: 0.625rem;
    color: ${({ theme }) => theme.jackpotModal.color};
`;

export const JackpotModalNote = styled.div`
    color: #fff;
    font-size: 0.85rem;
    position: absolute;
    bottom: 22px;
    font-family: Signika-Regular;
`;

const StarBurst = styled.img`
    position: absolute;
    bottom: 1.5rem;
    height: 15rem;
    image-rendering: -webkit-optimize-contrast;
`;

const StartBurstLeft = styled(StarBurst)`
    left: -2rem;
    transform: rotate(-54deg);
`;

const StartBurstRight = styled(StarBurst)`
    right: -2rem;
    transform: scaleX(-1) rotate(-54deg);
`;

const WinnersContainer = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 0.75rem;
    z-index: 1;
    width: 22.5rem;
    max-height: calc(3 * 5rem + 2 * 0.75rem);
    overflow-y: auto;
    background-color: ${({ theme }) => theme.jackpotModal.background};
    gap: 1.5rem;
    padding: 1.5rem 0;
    margin-top: 1rem;
`;

const NameAmountContainer = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 0 1.5rem;
`;

const NameText = styled.div`
    ${fontStyleCommon};

    color: ${(props) => props.theme.color};
`;

const AmountText = styled.div`
    ${fontStyleCommon};

    color: ${({ theme }) => theme.jackpotModal.color};
`;

export const JackpotModal: FC<JackpotModalProps> = ({
    winners,
    totalJackpotAmount,
    ...props
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { starBurstJackpot, headerBingo } = theme.images;
    const hasMultipleWinners = winners.length > 1;

    return (
        <CommonModal
            {...props}
            Container={JackpotModalContainer}
            className="ReactModal__Content--after-open-override"
        >
            <HeaderContainer>
                <JackpotModalHeader
                    src={headerBingo}
                    alt="Header Bingo Image"
                />
            </HeaderContainer>
            <JackpotModaTitle data-text={t('jackpotModal.hasBeenWon')}>
                {t('jackpotModal.hasBeenWon')}
            </JackpotModaTitle>
            <JackpotModalTotal>{totalJackpotAmount}</JackpotModalTotal>
            <StartBurstLeft
                src={starBurstJackpot}
                alt="Start Burst Jackpot Left Image"
            />
            <StartBurstRight
                src={starBurstJackpot}
                alt="Start Burst Jackpot Right Image"
            />
            <WinnersContainer>
                {winners.map((winner, index) => (
                    <NameAmountContainer key={index}>
                        <NameText>{winner.name}</NameText>
                        <AmountText>{winner.amount}</AmountText>
                    </NameAmountContainer>
                ))}
                {hasMultipleWinners && (
                    <JackpotModalNote>
                        Note: {t('jackpotModal.note')}
                    </JackpotModalNote>
                )}
            </WinnersContainer>
        </CommonModal>
    );
};
